/**
 * useTrackProjectNavigation Hook
 * Tracks the user's current page within a project by saving the URL to the backend.
 * Fire-and-forget — no loading state, no error handling needed.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { navigationService } from '../services/navigation.service';

// Match /projects/{uuid}/... pattern
const PROJECT_URL_REGEX = /^\/projects\/([0-9a-f-]{36})\/.+/i;

/**
 * Call this hook in MainLayout. It parses the project ID from the URL
 * and saves the current pathname to the backend on every project-page navigation.
 */
export function useTrackProjectNavigation(): void {
  const location = useLocation();
  const lastSavedUrl = useRef<string>('');

  useEffect(() => {
    const currentPath = location.pathname;

    // Skip if URL hasn't changed
    if (currentPath === lastSavedUrl.current) return;

    // Extract project ID from URL
    const match = currentPath.match(PROJECT_URL_REGEX);
    if (!match) return;

    const projectId = match[1];
    lastSavedUrl.current = currentPath;
    navigationService.saveLastVisited(projectId, currentPath);
  }, [location.pathname]);
}
